<template>
  <BaseModal :value="show" @input="$emit('close')">
    <template v-slot:header>
      <div class="text-xl font-bold">Stripe Payment</div>
    </template>
    <div class="p-6 w-full" style="max-width: 640px; margin: 0 auto;">
      <div v-if="loading" class="text-center py-8">
        <BaseMiniSpinner />
        <p class="text-base text-gray-600 mt-2">
          Loading payment from Stripe...
        </p>
      </div>

      <div
        v-else-if="loadError"
        class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded"
      >
        {{ loadError }}
        <button class="underline ml-2" @click="loadPayment">Retry</button>
      </div>

      <template v-else-if="payment">
        <!-- Resumen (datos en vivo de Stripe) -->
        <div class="p-4 bg-gray-50 rounded-lg space-y-1 text-base">
          <div class="flex justify-between">
            <span class="text-gray-600">Payment intent</span>
            <span class="font-mono">{{ payment.paymentIntentId }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Status</span>
            <span class="font-semibold">{{ payment.status }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Date</span>
            <span>{{ payment.created }}</span>
          </div>
          <div v-if="payment.card" class="flex justify-between">
            <span class="text-gray-600">Card</span>
            <span>
              {{ payment.card.brand.toUpperCase() }} ••••
              {{ payment.card.last4 }} ({{ payment.card.expMonth }}/{{
                payment.card.expYear
              }})
            </span>
          </div>
          <div v-if="payment.billingName" class="flex justify-between">
            <span class="text-gray-600">Cardholder</span>
            <span>{{ payment.billingName }}</span>
          </div>
          <div
            v-if="payment.receiptEmail || payment.billingEmail"
            class="flex justify-between"
          >
            <span class="text-gray-600">Email</span>
            <span>{{ payment.receiptEmail || payment.billingEmail }}</span>
          </div>
          <div v-if="payment.outcome" class="flex justify-between">
            <span class="text-gray-600">Outcome</span>
            <span>{{ payment.outcome }}</span>
          </div>
          <div class="border-t border-gray-300 my-2" />
          <div class="flex justify-between">
            <span class="text-gray-600">Captured</span>
            <span>{{ money(payment.amountCaptured) }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Refunded</span>
            <span :class="{ 'text-red-600': payment.amountRefunded > 0 }">
              {{ money(payment.amountRefunded) }}
            </span>
          </div>
          <div class="flex justify-between font-bold text-base">
            <span>Refundable</span>
            <span>{{ money(payment.refundable) }}</span>
          </div>
          <div v-if="payment.receiptUrl" class="text-right">
            <a
              :href="payment.receiptUrl"
              target="_blank"
              rel="noopener"
              class="text-blue-600 underline text-base"
              >View Stripe receipt</a
            >
          </div>
        </div>

        <div
          v-if="payment.disputed"
          class="mt-4 bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-2 rounded text-base"
        >
          This charge is disputed. Refunds must be handled from Stripe.
        </div>

        <!-- Reembolsos existentes -->
        <div v-if="payment.refunds.length > 0" class="mt-4">
          <h3 class="font-semibold mb-2">Refunds</h3>
          <div class="table-full">
            <table class="default-table text-base">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Reason</th>
                  <th>By</th>
                </tr>
              </thead>
              <tr v-for="refund in payment.refunds" :key="refund.id">
                <td>{{ refund.created }}</td>
                <td>{{ money(refund.amount) }}</td>
                <td
                  :class="{
                    'text-red-600':
                      refund.status === 'failed' || refund.status === 'canceled'
                  }"
                >
                  {{ refund.status }}
                  <template v-if="refund.failureReason">
                    ({{ refund.failureReason }})
                  </template>
                </td>
                <td>
                  {{ refund.reason || "-" }}
                  <div v-if="refund.note" class="text-base text-gray-500">
                    {{ refund.note }}
                  </div>
                </td>
                <td>{{ refund.adminEmail || "Stripe" }}</td>
              </tr>
            </table>
          </div>
        </div>

        <!-- Formulario de reembolso -->
        <div v-if="canRefund" class="mt-6 border-t pt-4">
          <h3 class="font-semibold mb-3">Refund</h3>
          <div class="flex gap-4 mb-3 text-base">
            <label class="flex items-center gap-1">
              <input type="radio" value="full" v-model="refundType" />
              Full ({{ money(payment.refundable) }})
            </label>
            <label class="flex items-center gap-1">
              <input type="radio" value="partial" v-model="refundType" />
              Partial
            </label>
          </div>
          <div v-if="refundType === 'partial'" class="mb-3">
            <label class="text-base text-gray-600">Amount</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              :max="payment.refundable"
              v-model="partialAmount"
              class="w-full border rounded px-3 py-2"
            />
          </div>
          <div class="mb-3">
            <label class="text-base text-gray-600">Reason</label>
            <select v-model="reason" class="w-full border rounded px-3 py-2">
              <option value="">-</option>
              <option value="requested_by_customer"
                >Requested by customer</option
              >
              <option value="duplicate">Duplicate</option>
              <option value="fraudulent">Fraudulent</option>
            </select>
          </div>
          <div class="mb-3">
            <label class="text-base text-gray-600">Internal note</label>
            <textarea
              v-model="note"
              rows="2"
              maxlength="500"
              class="w-full border rounded px-3 py-2"
            />
          </div>

          <div
            class="mb-3 px-3 py-2 rounded text-base bg-gray-50 border border-gray-200 text-gray-700"
          >
            <div v-if="linkedOrders.length === 1">
              Will be recorded on order #{{ linkedOrders[0].orderNumber }}.
            </div>
            <div v-else-if="linkedOrders.length > 1">
              This payment covers several orders:
              <span
                v-for="(order, index) in orderedLinkedOrders"
                :key="order.orderNumber"
                >{{ index > 0 ? ", " : "" }}#{{ order.orderNumber }} ({{
                  money(order.amount)
                }})</span
              >. The refund is recorded on them in this order, up to what each
              one paid.
            </div>
            <div v-else>This payment has no related order.</div>
            <div v-if="payment.posTransactionId">
              Will be recorded as a refund of POS payment #{{
                payment.posTransactionId
              }}.
            </div>
          </div>

          <div
            v-if="returnId"
            class="mb-3 px-3 py-2 rounded text-base"
            :class="
              exceedsReturnRefundable
                ? 'bg-yellow-100 border border-yellow-400 text-yellow-800'
                : 'bg-blue-50 border border-blue-200 text-blue-800'
            "
          >
            This refund will be recorded on the return
            <template v-if="returnRefundable !== null">
              (available for refund on the return:
              {{ money(returnRefundable) }})</template
            >.
            <template v-if="exceedsReturnRefundable">
              The amount is higher than the return's available refund.
            </template>
          </div>

          <div
            v-if="refundError"
            class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-3 text-base"
          >
            {{ refundError }}
          </div>

          <button
            class="w-full bg-red-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="refunding || refundAmount === null"
            @click="submitRefund"
          >
            <span v-if="refunding">Refunding...</span>
            <span v-else-if="refundAmount !== null"
              >Refund {{ money(refundAmount) }}</span
            >
            <span v-else>Enter a valid amount</span>
          </button>
        </div>
        <div
          v-else-if="!hasPermission"
          class="mt-4 text-base text-gray-500 text-center"
        >
          You don't have permission to refund payments.
        </div>
      </template>
    </div>
    <!-- Después de un refund: ofrecer enviar el Refund Receipt al cliente -->
    <SendReceiptModal
      v-if="receiptPrompt"
      is-refund
      :default-email="receiptPrompt.email"
      :send-fn="sendRefundReceipt"
      @close="closeReceiptPrompt"
    />
  </BaseModal>
</template>
<script>
import camelize from "camelize";
import SendReceiptModal from "@/components/payments/SendReceiptModal.vue";
import {
  sendPosReceipt,
  sendOrderPaymentReceipt
} from "@/api/store/PaymentsService.js";
import {
  getStripePayment,
  refundStripePayment
} from "@/api/store/StripeRefundsService.js";

function newIdempotencyKey() {
  if (window.crypto && typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 12)}`;
}

export default {
  components: { SendReceiptModal },
  props: {
    show: { type: Boolean, required: true },
    // Contexto desde donde se abre: una orden (orderNumber) o un cobro POS
    // (posTransactionId). Hace falta uno de los dos.
    orderNumber: { type: Number, required: false, default: null },
    posTransactionId: {
      type: [Number, String],
      required: false,
      default: null
    },
    paymentIntentId: { type: String, required: true },
    // Abierto desde un return: el refund también se registra en ese return.
    returnId: { type: String, required: false, default: null },
    // Disponible para reembolsar según el return (informativo; el límite
    // real lo pone Stripe).
    returnRefundable: { type: Number, required: false, default: null }
  },
  data() {
    return {
      loading: false,
      loadError: null,
      payment: null,
      refundType: "full",
      partialAmount: "",
      reason: "requested_by_customer",
      note: "",
      refunding: false,
      refundError: null,
      // { refund, email, posRefundId, orderRefundPaymentIds } mientras se
      // pregunta si enviar el recibo
      receiptPrompt: null,
      // Una key por intento: un doble click o reintento de red no genera dos
      // reembolsos en Stripe. Se renueva después de cada reembolso exitoso.
      idempotencyKey: newIdempotencyKey()
    };
  },
  computed: {
    token() {
      return this.$store.getters["auth/currentToken"];
    },
    hasPermission() {
      return this.$store.getters["auth/userHasPermission"](
        "can_refund_payment"
      );
    },
    canRefund() {
      return (
        this.hasPermission &&
        this.payment &&
        this.payment.canRefundPermission &&
        this.payment.status === "succeeded" &&
        !this.payment.disputed &&
        this.payment.refundableCents > 0
      );
    },
    linkedOrders() {
      return this.payment?.linkedOrders || [];
    },
    // Primero la orden del contexto, igual que el reparto del backend.
    orderedLinkedOrders() {
      return [...this.linkedOrders].sort((a, b) => {
        const aFirst = String(a.orderNumber) === String(this.orderNumber);
        const bFirst = String(b.orderNumber) === String(this.orderNumber);
        return aFirst === bFirst ? 0 : aFirst ? -1 : 1;
      });
    },
    exceedsReturnRefundable() {
      return (
        this.returnRefundable !== null &&
        this.refundAmount !== null &&
        this.refundAmount > this.returnRefundable
      );
    },
    // Monto a reembolsar en dólares, o null si el parcial no es válido.
    refundAmount() {
      if (!this.payment) return null;
      if (this.refundType === "full") return this.payment.refundable;
      const cents = Math.round(parseFloat(this.partialAmount) * 100);
      if (!cents || cents <= 0 || cents > this.payment.refundableCents) {
        return null;
      }
      return cents / 100;
    }
  },
  watch: {
    show: {
      immediate: true,
      handler(val) {
        if (val) this.loadPayment();
      }
    }
  },
  methods: {
    money(value) {
      const currency = this.payment?.currency === "USD" ? "$" : "";
      return `${currency}${Number(value || 0).toFixed(2)}`;
    },
    errorMessage(err) {
      return err.response?.data?.error ?? err.message;
    },
    resetForm() {
      this.refundType = "full";
      this.partialAmount = "";
      this.note = "";
      this.refundError = null;
    },
    async loadPayment() {
      if (!this.hasPermission) {
        this.loadError = "You don't have permission to refund payments.";
        return;
      }
      this.loading = true;
      this.loadError = null;
      try {
        this.payment = await getStripePayment(
          this.token,
          {
            orderNumber: this.orderNumber,
            posTransactionId: this.posTransactionId
          },
          this.paymentIntentId
        );
      } catch (err) {
        this.loadError = this.errorMessage(err);
      } finally {
        this.loading = false;
      }
    },
    // Recibo POS si el cobro tiene transacción POS (monto completo); si no, el
    // del pago de la orden. Usa los endpoints de recibo del admin.
    sendRefundReceipt(email) {
      const { posRefundId, orderRefundPaymentIds } = this.receiptPrompt;
      if (posRefundId) {
        return sendPosReceipt(posRefundId, email);
      }
      return sendOrderPaymentReceipt(orderRefundPaymentIds[0], email);
    },
    closeReceiptPrompt() {
      const refund = this.receiptPrompt.refund;
      this.receiptPrompt = null;
      this.$emit("refunded", refund);
    },
    async submitRefund() {
      if (this.refundAmount === null || this.refunding) return;
      const label = this.refundType === "full" ? "the full remaining " : "";
      if (
        !confirm(
          `Refund ${label}${this.money(
            this.refundAmount
          )} to the customer's card? This cannot be undone.`
        )
      ) {
        return;
      }
      this.refunding = true;
      this.refundError = null;
      try {
        const result = await refundStripePayment(this.token, {
          orderNumber: this.orderNumber,
          posTransactionId: this.posTransactionId,
          paymentIntentId: this.paymentIntentId,
          full: this.refundType === "full",
          amount: this.refundType === "partial" ? this.refundAmount : null,
          expectedRefundable: this.payment.refundableCents,
          reason: this.reason,
          note: this.note,
          idempotencyKey: this.idempotencyKey,
          returnId: this.returnId
        });
        this.idempotencyKey = newIdempotencyKey();
        if (result.payment) {
          this.payment = result.payment;
        } else {
          await this.loadPayment();
        }
        this.resetForm();
        this.$toast.open({
          message: `Refund ${result.refund.status}: ${this.money(
            result.refund.amount
          )}`,
          type: result.refund.status === "failed" ? "error" : "success",
          position: "top-right"
        });
        const receipt = result.receipt || {};
        const hasReceipt =
          !!receipt.posRefundId ||
          (receipt.orderRefundPaymentIds || []).length > 0;
        if (hasReceipt) {
          // "refunded" se emite al cerrar el aviso: el padre suele recargar y
          // desmontaría este modal con el aviso abierto.
          this.receiptPrompt = {
            refund: result.refund,
            email:
              receipt.email ||
              this.payment?.receiptEmail ||
              this.payment?.billingEmail ||
              "",
            posRefundId: receipt.posRefundId,
            orderRefundPaymentIds: receipt.orderRefundPaymentIds || []
          };
        } else {
          this.$emit("refunded", result.refund);
        }
      } catch (err) {
        this.refundError = this.errorMessage(err);
        // Sin respuesta (timeout/red) el reembolso pudo haberse creado: se
        // conserva la key para que un reintento no duplique. Con respuesta del
        // servidor el intento terminó y el siguiente usa una key nueva.
        if (err.response) {
          this.idempotencyKey = newIdempotencyKey();
        }
        // 409: el pago cambió en Stripe desde que se cargó; se muestran los
        // datos nuevos para que el admin decida de nuevo.
        if (err.response?.status === 409 && err.response.data?.payment) {
          this.payment = camelize(err.response.data.payment);
        }
      } finally {
        this.refunding = false;
      }
    }
  },
  // Si se cierra el modal (Esc / X) con el aviso del recibo abierto, igual se
  // avisa al padre que hubo un refund para que recargue.
  beforeDestroy() {
    if (this.receiptPrompt) {
      this.$emit("refunded", this.receiptPrompt.refund);
    }
  }
};
</script>
